import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { palette, WebsitePrimaryButton, TertiaryButton, NavigateBack as NavigateBackIcon } from '@dev/ee-components';
import { Dialog as MaterialDialog, Grid, Portal } from '@material-ui/core';

import { SetToastPortalWrapper } from '@/store/toast/actions';
import {
    CloseGallery,
    CloseEditorDragAndDrop,
    getCampainCreatorStore,
    SelectTemplate,
} from '@/store/campaignCreator/actions';
import { HideToast, ShowToast } from '@/store/toast/actions';
import DragDropEditor from '@/components/EditorDragDrop';
import LayoutSrc from '@/services/Router/LegacyDashRoute/template_editor_in_new_cc_layout.html';
import { HideMediaManager } from '@/store/mediaManager/actions';

const Overlay = styled.div`
    background: ${palette.snow[1]};
    height: max-content;

    .MuiContainer-root {
        padding-top: 60px;
    }
`;

const TopBarContainer = styled.div`
    width: 100%;
    display: flex;
    gap: 40px;
    justify-content: end;
    align-items: center;
    height: 68px;
    padding: 0 15px;
`;

enum TemplateVariant {
    NewFromDefault,
    Edit,
    CreateFromPublic,
    CreateFromPrivate,
}
const createDefaultHash = '/templates/create/dragdrop';
const createDefaultPath = `#${createDefaultHash}`;
const getTemplateVariant = (
    editTemplateName?: string,
    sourceTemplateName?: string,
    isPublic?: boolean,
    isFromGallery?: boolean
): { pathToLoad: string; templateVariant: TemplateVariant } => {
    if (sourceTemplateName && isPublic) {
        const createPublicPath = `#/templates/create/${safelyEncodeBase64Url(sourceTemplateName)}`;
        return {
            pathToLoad: createPublicPath,
            templateVariant: TemplateVariant.CreateFromPublic,
        };
    }

    if (sourceTemplateName && !isPublic && isFromGallery) {
        const createPrivatePath = `#/templates/edit/${safelyEncodeBase64Url(sourceTemplateName)}`;
        return {
            pathToLoad: createPrivatePath,
            templateVariant: TemplateVariant.CreateFromPrivate,
        };
    }

    if (editTemplateName) {
        const templateEditPath = editTemplateName ? `#/templates/edit/${safelyEncodeBase64Url(editTemplateName)}` : '';
        return {
            pathToLoad: templateEditPath,
            templateVariant: TemplateVariant.Edit,
        };
    }

    return {
        pathToLoad: createDefaultPath,
        templateVariant: TemplateVariant.NewFromDefault,
    };
};

export const EditorDragAndDropModal = (): JSX.Element => {
    const dispatch = useDispatch();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const { t } = useTranslation();
    const isStarted = useRef<boolean>(false);
    const isSetToLeave = useRef<boolean>(false);
    const iframeRef = useRef<HTMLIFrameElement>();
    const {
        editorDragAndDrop: { sourceTemplateName, isPublic, isEditedFromGallery },
        campaign,
        activeTemplateIndex,
        selectedTemplate,
    } = useSelector(getCampainCreatorStore);

    useEffect(() => {
        dispatch(SetToastPortalWrapper(true));

        return () => {
            dispatch(SetToastPortalWrapper(false));
        };
    }, []);

    const getTemplate = (): { templateid: number; name: string; isdirty?: boolean; isdefault: boolean } =>
        // @ts-expect-error ee global object
        iframeRef.current?.contentWindow?.window?.ee?.data?.selectedtemplate;

    const closeModals = (): void => {
        dispatch(CloseGallery());
        dispatch(HideMediaManager());
        dispatch(CloseEditorDragAndDrop());
    };

    const handleClose = (): void => {
        const template = getTemplate();
        if (template?.templateid) {
            dispatch(SelectTemplate(template.name, false, false));
        }
        closeModals();
    };

    const leave = (): void => {
        if (isSaving) {
            dispatch(
                ShowToast({
                    message: t('templateisbeingsaved'),
                    severity: 'info',
                    duration: 6000,
                    width: 380,
                    onClose: HideToast,
                })
            );
            return;
        }
        handleClose();
    };

    const close = (): void => {
        const template = getTemplate();
        if (!template?.templateid) {
            isSetToLeave.current = true;
            // @ts-expect-error ee global object
            iframeRef.current?.contentWindow?.window?.ee.editor.save();
            return;
        }
        handleClose();
    };

    const editTemplateName = campaign?.CampaignTemplates?.[activeTemplateIndex]?.TemplateName ?? '';
    const isFromGallery = selectedTemplate?.[activeTemplateIndex]?.isFromGallery || isEditedFromGallery;
    const { pathToLoad, templateVariant } = getTemplateVariant(
        editTemplateName,
        sourceTemplateName,
        isPublic,
        isFromGallery
    );

    const prepareTemplate = (w: Window): void => {
        if (templateVariant !== TemplateVariant.Edit) {
            // @ts-expect-error ee global object
            w.ee.data.selectedtemplate.name = `${
                campaign.Name ? campaign.Name.substring(0, 80) : t('campaign')
            }: ${dayjs().format('YYYY-MM-DD h:mm:ss')}`;
            // @ts-expect-error ee global object
            w.ee.container.find('input[name="name"]').val(w.ee.data.selectedtemplate.name);
            // @ts-expect-error ee global object
            w.ee.container.find('.js-template-name').html(w.ee.data.selectedtemplate.name);
        }
        if (templateVariant === TemplateVariant.CreateFromPrivate) {
            // @ts-expect-error ee global object
            w.ee.data.selectedtemplate.originaltemplateid = w.ee.data.selectedtemplate.templateid;
            // @ts-expect-error ee global object
            w.ee.data.selectedtemplate.templateid = 0;
            // @ts-expect-error ee global object
            w.ee.changeHashQuietly(createDefaultHash);
        }
        // @ts-expect-error ee global object
        w.ee.data.selectedtemplate.templatescope = 2; // Used-In-Campaign Type
    };

    const attachEditorCallback = (w: Window): void => {
        w?.document?.addEventListener('templateSaving', () => {
            setIsSaving(true);
        });

        w?.document?.addEventListener('templateSaved', () => {
            if (isSetToLeave.current) {
                handleClose();
                return;
            }
            setIsSaving(false);
        });

        w?.document?.addEventListener('templateEditorNameChanged', () => {
            setIsSaving(true);
        });

        w?.document?.addEventListener('templateSaveError', () => {
            setIsSaving(false);
        });

        w?.document?.addEventListener('templateEditorCoreInitStart', () => {
            prepareTemplate(w);
        });

        /* this listener seems to be redundant,
         ** the logic except setIsSaving is executed before callback is triggered
         ** by custom event, isSetToLeave.current is already false
         ** and setIsSaving is blocking continue logic wihtout applying changes
         */
        // w?.document?.addEventListener('templateEditorCoreInitEnd', () => {
        //     // @ts-expect-error ee global object
        //     w.ee.editor.core.changeCallback = (params) => {
        //         // @ts-expect-error ee global object
        //         w.ee.data.selectedtemplate.isdirty = true;
        //         // @ts-expect-error ee global object
        //         w.ee.editor.isDirty(params);
        //         isSetToLeave.current = false;
        //         setIsSaving(true);
        //     };
        // });

        w?.addEventListener('hashchange', (): void => {
            // @ts-expect-error ee global object
            const prevPath = w.ee.Router.prevPath();
            const hash = w.location.hash;
            if (
                isStarted.current &&
                (hash === '#/templates' ||
                    hash === '#/dashboard' ||
                    hash === '#/templates/create/dragdrop' ||
                    (hash.includes('#/templates/create/') && prevPath.includes('templates/edit/')) ||
                    (hash.includes('#/templates/edit/') &&
                        prevPath !== 'dashboard' &&
                        prevPath !== 'templates/create/dragdrop' &&
                        !prevPath.includes('templates/create/')) ||
                    hash?.includes('#/templates/gallery'))
            ) {
                closeModals();
            }
            if (
                !isStarted.current &&
                ((templateVariant !== TemplateVariant.CreateFromPrivate && hash === pathToLoad) ||
                    (templateVariant === TemplateVariant.CreateFromPrivate && hash === createDefaultPath))
            ) {
                isStarted.current = true;
            }
        });
    };

    // scroll container have to be added as query selector string (DOM node mentioned in react-lazyload documentation doesn't work)
    const scrollContainerId = 'editorDragAndDropScrollContainer';

    return (
        <Portal>
            <MaterialDialog open={true} fullScreen={true} scroll={'body'} id={scrollContainerId}>
                <Overlay>
                    <TopBarContainer>
                        <TertiaryButton
                            onClick={leave}
                            text={t('backtocampaign')}
                            startIcon={<NavigateBackIcon color={palette.indigo[5]} />}
                        />
                        <WebsitePrimaryButton onClick={close} text={t('continue')} disabled={isSaving} />
                    </TopBarContainer>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} sm={12}>
                            <DragDropEditor
                                overwritePath={pathToLoad}
                                overwriteIframeRef={iframeRef}
                                overwriteHeight="calc(100vh - 68px)"
                                onloadCallback={attachEditorCallback}
                                overwriteLayoutSrc={LayoutSrc}
                            />
                        </Grid>
                    </Grid>
                </Overlay>
            </MaterialDialog>
        </Portal>
    );
};
